import { ChevronLeftIcon } from '@gurupay/gp-ui-library';
import clsx from 'clsx';
import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';

interface CarouselProps<T> {
	items: T[];
	renderSlide: (item: T) => ReactNode;
	isLoading?: boolean;
	loadingSlot?: ReactNode;
	emptySlot?: ReactNode;
}

export const Carousel = <T,>({
	items,
	renderSlide,
	isLoading,
	loadingSlot,
	emptySlot,
}: CarouselProps<T>) => {
	const [index, setIndex] = useState(0);

	useEffect(() => {
		if (index >= items.length) {
			setIndex(Math.max(0, items.length - 1));
		}
	}, [items.length, index]);

	if (isLoading) return <>{loadingSlot ?? null}</>;
	if (!items.length) return <>{emptySlot ?? null}</>;

	const currentItem = items[index];
	if (!currentItem) return <>{emptySlot ?? null}</>;

	const handlePrev = () => setIndex((i) => i - 1);
	const handleNext = () => setIndex((i) => i + 1);

	return (
		<div className="flex flex-col gap-space-8 mt-space-16">
			<div key={index} className="animate-fade-in">
				{renderSlide(currentItem)}
			</div>
			{items.length > 1 && (
				<div className="flex justify-center items-center gap-space-4">
					<button
						aria-label="Previous"
						className="p-space-4 rounded-full hover:bg-neutral-100 transition-colors disabled:opacity-30"
						disabled={!index}
						onClick={handlePrev}
						type="button"
					>
						<ChevronLeftIcon className="w-space-20 h-space-20 text-neutral-600" />
					</button>
					<div className="flex items-center gap-space-4">
						{items.map((_, i) => (
							<button
								key={i}
								aria-label={`Go to slide ${i + 1}`}
								className={clsx('rounded-full transition-all duration-200', {
									'w-space-16 h-space-8 bg-neutral-600': i === index,
									'w-space-8 h-space-8 bg-neutral-200 hover:bg-neutral-400': i !== index,
								})}
								onClick={() => setIndex(i)}
								type="button"
							/>
						))}
					</div>
					<button
						aria-label="Next"
						className="p-space-4 rounded-full hover:bg-neutral-100 transition-colors disabled:opacity-30"
						disabled={index === items.length - 1}
						onClick={handleNext}
						type="button"
					>
						<ChevronLeftIcon className="rotate-180 w-space-20 h-space-20 text-neutral-600" />
					</button>
				</div>
			)}
		</div>
	);
};
